import type { GetterTree, MutationTree, ActionTree, Module } from 'vuex';
import type { RootState } from '@/store';
import { actionNames, getterNames, mutationNames, namespace } from './types';
import { ApiException, TrainingPlanDto, TraineeClientinstance } from '@/api';

const state = {
    loading: false,
    error: null as ApiException | null,
    newContact: null as TrainingPlanDto | null,
};

export type TraineeState = typeof state;

const getters: GetterTree<TraineeState, RootState> = {
    [getterNames.getNewTrainee]: (state) => state.newContact,
    [getterNames.getLoading]: (state) => state.loading,
    [getterNames.getError]: (state) => state.error,
};

const mutations: MutationTree<TraineeState> = {
    [mutationNames.setLoading](state, loading: boolean) {
        state.loading = loading;
    },
    [mutationNames.setError](state, error: ApiException | null) {
        state.error = error;
    },
    [mutationNames.setTrainee](state, trainee: TrainingPlanDto | null) {
        state.newContact = trainee;
    },
};

const actions: ActionTree<TraineeState, RootState> = {
    async [actionNames.createNewTrainee](context, formData: FormData) {
        try {
            context.commit(mutationNames.setLoading, true);
            const fName = (formData.get('fName') || '').toString();
            const lName = (formData.get('lName') || '').toString();
            const dob = (formData.get('dob') || '').toString();
            const gender = (formData.get('gender') || '').toString();
            const proofFile = formData.get('proofFile') as File;

            const client = TraineeClientinstance as unknown as Record<
                string,
                any
            >;
            const createFn =
                client.create ?? client.createTrainee ?? client.post;

            if (typeof createFn !== 'function') {
                throw new Error(
                    'No trainee create method exists on TrainingPlanClient.',
                );
            }

            const traineeResponse = await createFn.call(
                TraineeClientinstance,
                fName,
                lName,
                dob,
                gender,
                { data: proofFile, fileName: proofFile.name },
            );

            context.commit(
                mutationNames.setTrainee,
                traineeResponse?.result ?? null,
            );
            context.commit(mutationNames.setError, null);
        } catch (e) {
            context.commit(mutationNames.setTrainee, null);
            context.commit(mutationNames.setError, e as ApiException);
        } finally {
            context.commit(mutationNames.setLoading, false);
        }
    },
};

const module: Module<TraineeState, RootState> = {
    state: () => state,
    getters,
    mutations,
    actions,
    namespaced: true,
};

export { namespace, getterNames, actionNames, mutationNames };
export default module;
